import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';
import useCalendarEntitlement from '../hooks/useCalendarEntitlement';

function AdminDashboard() {
  const { getTenantSubdomain, currentTenant } = useConfig();
  const navigate = useNavigate();
  const calendarAccess = useCalendarEntitlement();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeLoans: 0,
    totalCustomers: 0,
    overdueLoans: 0,
    loansInDefault: 0,
    revenueLast30Days: 0,
    revenueTrend: 'flat',
    collectionRate: 0,
    taxDeadlines: [],
    // Assume configured until the server says otherwise. The prompt below is a
    // setup task, and flashing it for a second on every load for tenants who
    // finished setup months ago would train people to ignore it.
    financingConfigured: true,
    draftProperties: 0,
    // Left empty rather than defaulted to 'free_trial'. The sample-data control
    // is destructive, and defaulting it on would show the button to a paying
    // tenant for the moment before the first response lands.
    subscriptionTier: ''
  });
  const [loading, setLoading] = useState(true);
  const [clearingSampleData, setClearingSampleData] = useState(false);

  useEffect(() => {
    // Check if admin is logged in
    const adminToken = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');

    if (!adminToken || !adminUser) {
      navigate('/admin/login');
      return;
    }

    setAdmin(JSON.parse(adminUser));
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSampleData = async () => {
    // Native confirm rather than a modal: this page has no dialog primitive, and
    // a one-line browser confirm is the same on a phone as on a desktop.
    const confirmed = window.confirm(
      'This removes the example properties, customers, and loans. Your own data is not touched.'
    );
    if (!confirmed) return;

    setClearingSampleData(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/sample-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh rather than patching the counts locally: the purge spans six
      // tables and every tile on this page reads from at least one of them, so
      // re-asking the server is the only way they all stay consistent.
      await loadStats();
      alert(response.data?.message || 'Sample data cleared.');
    } catch (err) {
      // A 409 means one of the tenant's own records still points at an example
      // row, and the server's message says which. That is worth showing them
      // verbatim; anything else gets a generic line.
      const serverMessage = err.response?.data?.error;
      alert(serverMessage || 'Could not clear the sample data. Please try again.');
      console.error('Failed to clear sample data:', err);
    } finally {
      setClearingSampleData(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  if (!admin) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-dashboard-container">
      {/* Admin Header */}
      <div className="admin-dashboard-header">
        <div className="admin-dashboard-header-text">
          <h1 className="admin-page-title">🌿 {admin.tenantName || 'DeedStack'} Admin</h1>
          <p>
            Welcome back, {admin.firstName} {admin.lastName}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/admin/help')}
            className="btn admin-help-btn"
            title="Help & Documentation"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              backgroundColor: 'var(--color-background-alt)',
              border: '1px solid var(--color-brand-text)',
              color: 'var(--color-brand-text)',
              cursor: 'pointer'
            }}
          >
            ?
          </button>
          <button
            onClick={handleLogout}
            className="btn admin-logout-btn"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Finish setup: financing options.
          Shown ONLY here and ONLY while unconfigured -- deliberately not a
          site-wide banner. A tenant lands on this page after logging in, so one
          prompt at the top of it is seen without being nagged on every screen,
          and it disappears the moment the first tier is saved. */}
      {!loading && !stats.financingConfigured && (
        <div className="card" style={{
          marginBottom: '20px',
          padding: '20px 24px',
          background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-warning-bg) 100%)',
          border: '1px solid var(--color-warning-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: '1 1 420px' }}>
            <span style={{ fontSize: '1.5rem', lineHeight: 1.2 }}>⚙️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--color-warning-text)', marginBottom: '4px' }}>
                Set up your financing options to start listing properties
              </div>
              <p style={{ margin: 0, fontSize: '0.92rem', color: 'var(--color-warning-text)', lineHeight: 1.5 }}>
                You have not set a down payment or interest rate yet. These are your terms, the
                price a buyer is quoted and signs for, so they are yours to set and DeedStack
                does not fill them in for you. Everything else works in the meantime: you can add
                properties and they will be saved as drafts.
                {stats.draftProperties > 0 && (
                  <>
                    {' '}
                    <strong>
                      {stats.draftProperties} propert{stats.draftProperties === 1 ? 'y is' : 'ies are'} waiting
                      to be published.
                    </strong>
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/settings/business')}
            className="btn btn-primary"
            style={{ whiteSpace: 'nowrap', padding: '10px 20px' }}
          >
            Set Up Financing
          </button>
        </div>
      )}

      {/* Your Website URL */}
      {getTenantSubdomain() && (() => {
        // Use custom domain if available, otherwise subdomain
        const websiteUrl = currentTenant?.customDomain
          ? `https://${currentTenant.customDomain}`
          : `https://${getTenantSubdomain()}.deedstack.com`;
        const displayDomain = currentTenant?.customDomain
          ? currentTenant.customDomain
          : `${getTenantSubdomain()}.deedstack.com`;

        return (
          <div className="card" style={{
            marginBottom: '20px',
            padding: '15px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px',
            background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-info-bg) 100%)',
            border: '1px solid var(--color-info-border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.3rem' }}>🌐</span>
              <div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '2px' }}>Your Website</div>
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--color-brand-text)',
                    fontWeight: '600',
                    fontSize: '1.05rem',
                    textDecoration: 'none'
                  }}
                >
                  {displayDomain}
                </a>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => window.open(websiteUrl, '_blank')}
                className="btn btn-secondary"
                style={{ fontSize: '0.9rem', padding: '8px 16px' }}
              >
                Visit Site
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(websiteUrl);
                  alert('URL copied to clipboard!');
                }}
                className="btn btn-secondary"
                style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                title="Copy URL"
              >
                Copy URL
              </button>
            </div>
          </div>
        );
      })()}

      {/* Clear sample data.
          Trial only, and only once the tier is actually known -- `loading` is
          still true on the first paint, and flashing a destructive control for
          a moment before hiding it again is worse than showing it a beat late.
          The numbers in the stats grid below are the example rows until this is
          used, which is why the control sits directly above them. */}
      {!loading && stats.subscriptionTier === 'free_trial' && (
        <div className="card" style={{
          marginBottom: '20px',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              Example data
            </div>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-light)', lineHeight: 1.5 }}>
              Your trial started with a few example properties, customers and loans so the
              dashboard was not empty. Clear them whenever you are ready. They are removed
              automatically when you subscribe.
            </p>
          </div>
          <button
            onClick={handleClearSampleData}
            disabled={clearingSampleData}
            className="btn btn-secondary"
            style={{
              minHeight: '44px',
              padding: '10px 20px',
              fontSize: '0.95rem',
              width: '100%',
              maxWidth: '220px',
              cursor: clearingSampleData ? 'wait' : 'pointer',
              opacity: clearingSampleData ? 0.6 : 1
            }}
          >
            {clearingSampleData ? 'Clearing...' : 'Clear sample data'}
          </button>
        </div>
      )}

      {/* Quick Stats */}
      <div className="admin-stats-grid">
        <div className="card admin-stat-card">
          <h3 className="admin-stat-number">
            {loading ? '-' : stats.totalProperties}
          </h3>
          <p className="admin-stat-label">Total Properties</p>
        </div>
        <div className="card admin-stat-card">
          <h3 className="admin-stat-number">
            {loading ? '-' : stats.activeLoans}
          </h3>
          <p className="admin-stat-label">Active Loans</p>
        </div>
        <div className="card admin-stat-card">
          <h3 className="admin-stat-number">
            {loading ? '-' : stats.totalCustomers}
          </h3>
          <p className="admin-stat-label">Total Customers</p>
        </div>
      </div>

      {/* Performance Indicators */}
      <h2 className="admin-section-title">📊 Performance Overview</h2>
      <div className="admin-performance-grid">
        <div className="card admin-performance-card">
          <div className="admin-performance-header">
            <h4>Revenue (Last 30 Days)</h4>
            <span>
              {stats.revenueTrend === 'up' && '📈'}
              {stats.revenueTrend === 'down' && '📉'}
              {stats.revenueTrend === 'flat' && '➡️'}
            </span>
          </div>
          <h3 className="admin-performance-number" style={{ 
            color: stats.revenueTrend === 'up' ? 'var(--forest-green)' : stats.revenueTrend === 'down' ? '#dc3545' : '#666' 
          }}>
            ${loading ? '-' : stats.revenueLast30Days.toFixed(2)}
          </h3>
          <p className="admin-performance-subtitle">
            {stats.revenueTrend === 'up' && 'Trending up vs. previous period'}
            {stats.revenueTrend === 'down' && 'Trending down vs. previous period'}
            {stats.revenueTrend === 'flat' && 'Stable vs. previous period'}
          </p>
        </div>

        <div className="card admin-performance-card">
          <h4 style={{ margin: '0 0 10px 0', color: 'var(--color-text-light)', fontSize: '14px' }}>Collection Rate</h4>
          <div className="admin-performance-flex">
            <h3 className="admin-performance-number" style={{ 
              color: stats.collectionRate >= 90 ? 'var(--forest-green)' : stats.collectionRate >= 75 ? '#ffc107' : '#dc3545' 
            }}>
              {loading ? '-' : stats.collectionRate}%
            </h3>
            <span className="admin-performance-icon">
              {stats.collectionRate >= 90 && '✅'}
              {stats.collectionRate >= 75 && stats.collectionRate < 90 && '⚠️'}
              {stats.collectionRate < 75 && '🚨'}
            </span>
          </div>
          <p className="admin-performance-subtitle">On-time payment rate (30 days)</p>
        </div>

        <div className="card admin-performance-card" style={{ 
          backgroundColor: stats.overdueLoans > 0 ? '#fff3cd' : 'white', 
          border: stats.overdueLoans > 0 ? '2px solid #ffc107' : '1px solid #ddd' 
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: 'var(--color-text-light)', fontSize: '14px' }}>Overdue Loans</h4>
          <h3 className="admin-performance-number" style={{ 
            color: stats.overdueLoans === 0 ? 'var(--forest-green)' : '#ffc107' 
          }}>
            {loading ? '-' : stats.overdueLoans}
          </h3>
          <p className="admin-performance-subtitle">
            {stats.overdueLoans === 0 ? 'All payments current 🎉' : 'Requires attention'}
          </p>
        </div>

        <div className="card admin-performance-card" style={{ 
          backgroundColor: stats.loansInDefault > 0 ? '#f8d7da' : 'white', 
          border: stats.loansInDefault > 0 ? '2px solid #dc3545' : '1px solid #ddd' 
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: 'var(--color-text-light)', fontSize: '14px' }}>In Default</h4>
          <h3 className="admin-performance-number" style={{ 
            color: stats.loansInDefault === 0 ? 'var(--forest-green)' : '#dc3545' 
          }}>
            {loading ? '-' : stats.loansInDefault}
          </h3>
          <p className="admin-performance-subtitle">
            {stats.loansInDefault === 0 ? 'No defaults 🎉' : 'Notices sent'}
          </p>
        </div>
      </div>

      {/* Upcoming Tax Deadlines */}
      {stats.taxDeadlines && stats.taxDeadlines.length > 0 && (
        <>
          <h2 className="admin-section-title">📅 Tax Installments (next 60 days, plus anything unpaid)</h2>
          <div className="card admin-tax-deadlines-card">
            {stats.taxDeadlines.map((deadline, idx) => {
              // One row per instalment now, and the server says whether a
              // disbursement covers it. A past instalment stays on this card
              // until one does, which is the whole point: the old card dropped
              // a deadline the morning it passed, paid or not.
              const due = deadline.due_date ? new Date(deadline.due_date) : null;
              const isPastDue = Boolean(deadline.past_due) && !deadline.paid;

              const badge = {
                display: 'inline-block',
                marginLeft: '8px',
                padding: '1px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 'bold',
                background: deadline.paid ? '#e9f7ef' : isPastDue ? '#fdecea' : '#fff4e5',
                color: deadline.paid ? '#1b7a43' : isPastDue ? '#c62828' : '#b35309'
              };

              return (
                <div key={idx} className="admin-tax-deadline-item">
                  <div className="admin-tax-deadline-info">
                    <strong>{deadline.title}</strong>
                    <div
                      className="admin-tax-deadline-payment"
                      style={isPastDue ? { color: 'var(--color-error-text)', fontWeight: 'bold' } : undefined}
                    >
                      Payment {deadline.instalment}: {due ? due.toLocaleDateString() : 'no date set'}
                      {' - '}${parseFloat(deadline.amount || 0).toFixed(2)}
                      <span style={badge}>
                        {deadline.paid ? 'Paid' : isPastDue ? 'Past due' : 'Unpaid'}
                      </span>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate(`/admin/properties/${deadline.id}`)}
                    className="btn btn-secondary admin-tax-deadline-btn"
                  >
                    View Property
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Admin Menu - Category Hubs */}
      <h2 className="admin-section-title">Quick Access</h2>
      <div className="admin-menu-grid">
        <div className="card admin-menu-card" onClick={() => navigate('/admin/properties-hub')}>
          <div className="admin-hub-card-icon">🏠</div>
          <h2>Properties</h2>
          <p>Property listings and state management</p>
          <button className="btn btn-primary">Manage Properties</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/customers-loans-hub')}>
          <div className="admin-hub-card-icon">👥</div>
          <h2>Customers & Loans</h2>
          <p>Customer accounts, loans, and payment tracking</p>
          <button className="btn btn-primary">Manage Customers</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/reports-hub')}>
          <div className="admin-hub-card-icon">📈</div>
          <h2>Reports</h2>
          <p>Financial reports and tax documentation</p>
          <button className="btn btn-primary">View Reports</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings-hub')}>
          <div className="admin-hub-card-icon">⚙️</div>
          <h2>Settings</h2>
          <p>Branding, business config, features, and content</p>
          <button className="btn btn-primary">Configure</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/tools-hub')}>
          <div className="admin-hub-card-icon">🛠️</div>
          <h2>Tools</h2>
          <p>eBay listings, auctions, and property sourcing</p>
          <button className="btn btn-primary">Open Tools</button>
        </div>

        {/*
          The Auction Calendar, for tenants whose plan includes it.

          `ready` is checked as well as `included` so an entitled tenant does
          not watch the card pop in after the rest of the grid has settled.
          Hiding it is presentation only: /admin/calendar is gated on the
          server, and a tenant who types the URL gets the upgrade page.
        */}
        {calendarAccess.ready && calendarAccess.included && (
          <div className="card admin-menu-card" onClick={() => navigate('/admin/calendar')}>
            <div className="admin-hub-card-icon">🗺️</div>
            <h2>Auction Calendar</h2>
            <p>County sale dates across every live state</p>
            <button className="btn btn-primary">Open Calendar</button>
          </div>
        )}

        <div className="card admin-menu-card" onClick={() => navigate('/admin/account-hub')}>
          <div className="admin-hub-card-icon">💎</div>
          <h2>Account</h2>
          <p>Subscription tier, billing, and account settings</p>
          <button className="btn btn-primary">Manage Account</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/help')}>
          <div className="admin-hub-card-icon">❓</div>
          <h2>Help & Docs</h2>
          <p>Documentation, guides, and support resources</p>
          <button className="btn btn-primary">Get Help</button>
        </div>

        <div className="card admin-menu-card admin-menu-card-disabled">
          <div className="admin-hub-card-icon">🎧</div>
          <h2>Support</h2>
          <p>Contact DeedStack™ support or submit a ticket</p>
          <button className="btn btn-disabled" disabled>Coming Soon</button>
        </div>

        <div className="card admin-menu-card admin-menu-card-disabled">
          <div className="admin-hub-card-icon">🔗</div>
          <h2>Integrations</h2>
          <p>Connect with external services and APIs</p>
          <button className="btn btn-disabled" disabled>Coming Soon</button>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;